import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCountry } from '../../hooks/useCountry';

const About = () => {
  const { theme, isSubdomain } = useCountry();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const colors = theme?.colors || {};

  const steps = [
    { num: '1', title: t('pages.about.step1Title'), desc: t('pages.about.step1Desc') },
    { num: '2', title: t('pages.about.step2Title'), desc: t('pages.about.step2Desc') },
    { num: '3', title: t('pages.about.step3Title'), desc: t('pages.about.step3Desc') },
  ];

  const benefits = [
    { title: t('pages.about.legal'), desc: t('pages.about.legalDesc') },
    { title: t('pages.about.affordable'), desc: t('pages.about.affordableDesc') },
    { title: t('pages.about.quick'), desc: t('pages.about.quickDesc') },
    { title: t('pages.about.secure'), desc: t('pages.about.secureDesc') },
  ];

  return (
    <div className={isSubdomain ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'}>
      {/* Header - only on main domain */}
      {!isSubdomain && (
        <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex-shrink-0">
                <h1 className="text-xl sm:text-2xl font-bold text-blue-600">RentyDoc</h1>
              </Link>
              <div className="hidden md:flex items-center space-x-4">
                <Link to="/about" className="text-blue-600 px-3 py-2 text-sm font-medium">About</Link>
                <Link to="/contact" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Contact</Link>
                <Link to="/login" className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">Sign Up</Link>
              </div>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                </svg>
              </button>
            </div>
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-100 pt-4 space-y-2">
                <Link to="/about" className="block px-4 py-3 text-blue-600 bg-blue-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
                <Link to="/contact" className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <Link to="/login" className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                <Link to="/register" className="block px-4 py-3 bg-blue-600 text-white text-center rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Hero */}
      <section className="text-white py-12 sm:py-16 lg:py-20" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">{t('pages.about.title')}</h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto">
            {t('pages.about.subtitle')}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Mission */}
        <section className="mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">{t('pages.about.mission')}</h2>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/50 shadow-sm">
            <p className="text-base sm:text-lg text-gray-700 mb-4">
              {t('pages.about.missionText1')}
            </p>
            <p className="text-base sm:text-lg text-gray-700">
              {t('pages.about.missionText2')}
            </p>
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">{t('pages.about.howItWorks')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {steps.map((step) => (
              <div key={step.num} className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/50 shadow-sm">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-lg" style={{ background: theme ? colors.buttonBg : `linear-gradient(135deg, #6366f1, #8b5cf6)`, boxShadow: `0 10px 25px -5px ${colors.primary || '#6366f1'}30` }}>
                  <span className="text-xl font-bold text-white">{step.num}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900">{step.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Coverage */}
        <section className="mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">{t('pages.about.coverage')}</h2>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/50 shadow-sm">
            <p className="text-base sm:text-lg text-gray-700 mb-6">
              {t('pages.about.coverageText')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {[
                { color: '#1B3A5C', name: 'United States', law: 'State Statutes' },
                { color: '#C41E3A', name: 'Canada', law: 'Residential Tenancies Act' },
                { color: '#1B2A4A', name: 'United Kingdom', law: 'Housing Act 1988' },
                { color: '#006D6F', name: 'Australia', law: 'Fair Trading' },
                { color: '#002654', name: 'France', law: 'Loi ALUR' },
                { color: '#2C2C2C', name: 'Germany', law: 'BGB § 535' },
                { color: '#1B5E20', name: 'Italy', law: 'Legge 431/98' },
                { color: '#C41E3A', name: 'Spain', law: 'LAU 1994' },
              ].map((country) => (
                <div key={country.name} className="bg-white rounded-xl p-3 sm:p-4 text-center border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: `${country.color}18` }}>
                    <svg className="w-4 h-4" style={{ color: country.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="font-semibold text-gray-800 text-xs sm:text-sm">{country.name}</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">{country.law}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-500 mt-4 sm:mt-6 text-center text-sm">{t('pages.about.coverageMore')}</p>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">{t('pages.about.whyChoose')}</h2>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/50 shadow-sm">
            <div className="space-y-4 sm:space-y-5">
              {benefits.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary || '#16a34a'}15` }}>
                    <svg className="w-5 h-5" style={{ color: colors.primary || '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 text-sm sm:text-base">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-2xl p-6 sm:p-10 text-center relative overflow-hidden" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>
          <div className="relative">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{t('pages.about.ctaTitle')}</h2>
            <p className="text-white/80 mb-5 sm:mb-6 text-sm sm:text-base max-w-lg mx-auto">
              {t('pages.about.ctaText')}
            </p>
            <Link
              to="/contracts/create"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-white rounded-xl font-semibold hover:bg-gray-50 transition-colors active:scale-[0.98] shadow-lg"
              style={{ color: colors.primary || '#6366f1' }}
            >
              {t('pages.about.ctaButton')}
            </Link>
          </div>
        </section>
      </div>

      {/* Footer - only on main domain */}
      {!isSubdomain && (
        <footer className="bg-white border-t border-gray-200 mt-8 sm:mt-12">
          <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10">
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-6">
              <Link to="/terms" className="text-gray-600 hover:text-gray-900 text-sm">Terms</Link>
              <Link to="/privacy" className="text-gray-600 hover:text-gray-900 text-sm">Privacy</Link>
              <Link to="/about" className="text-blue-600 text-sm font-medium">About</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 text-sm">Contact</Link>
            </div>
            <p className="text-gray-500 text-sm text-center">&copy; 2025 RentyDoc. All rights reserved.</p>
          </div>
        </footer>
      )}
    </div>
  );
};

export default About;
